/**
 * Environment identifier used by `MontillaLoadConfig.app.environment` to
 * pick the rum-api host. Mirrors the union in `types/index.ts`.
 *
 * `'test'` is reserved for unit / E2E tests — it enables the bundled-JSON
 * mappings fallback (other envs leave the destination inert if the remote
 * fails) and points to a non-routable host so accidental network calls
 * fail fast.
 */
export type Environment = 'development' | 'production' | 'homolog' | 'test'

/**
 * Base hosts of the Montilla rum-api per environment. Both the event
 * collector (`/collect`) and the mappings endpoint
 * (`/destinations/mappings.json`) live under these origins.
 *
 * `development` falls back to the homolog host — there is no separate dev
 * backend today, and Faro keeps using `ConsoleTransport` in development so
 * events don't actually hit the network unless the user overrides things.
 *
 * `test` uses a non-routable host (`montilla.test`); tests should mock fetch
 * or rely on the bundled fallback, which is only active under this env.
 */
export const MONTILLA_HOSTS: Record<Environment, string> = {
  production: 'https://montilla-rum.quero.space',
  homolog: 'https://montilla-rum-homolog.quero.space',
  development: 'https://montilla-rum-homolog.quero.space',
  test: 'https://montilla.test',
} as const

export const COLLECTOR_PATH = '/collect' as const
export const MAPPINGS_PATH = '/destinations/mappings.json' as const
/**
 * Remote per-client config endpoint. Lives under the same rum-api host as the
 * mappings endpoint. Fetched (PUBLIC) when `load({ clientKey })` is set.
 */
export const CLIENT_CONFIG_PATH = '/clients/config.json' as const

/**
 * Build the full collector URL for the given environment.
 *
 * `override`, if provided, short-circuits the env-derived default — useful
 * during host transitions, canary deploys, or when a consumer points at a
 * staging endpoint of its own. Without it, the URL tracks `MONTILLA_HOSTS[env]`.
 */
export function resolveCollectorUrl(env: Environment, override?: string): string {
  return override ?? `${MONTILLA_HOSTS[env]}${COLLECTOR_PATH}`
}

/**
 * Build the full mappings URL for the given environment. See
 * `resolveCollectorUrl` for `override` semantics.
 */
export function resolveMappingsUrl(env: Environment, override?: string): string {
  return override ?? `${MONTILLA_HOSTS[env]}${MAPPINGS_PATH}`
}

/**
 * Build the full client-config URL for the given environment. See
 * `resolveCollectorUrl` for `override` semantics. The loader appends
 * `?key=<clientKey>&env=<env>`.
 */
export function resolveClientConfigUrl(env: Environment, override?: string): string {
  return override ?? `${MONTILLA_HOSTS[env]}${CLIENT_CONFIG_PATH}`
}

// ── Legacy production-targeted exports (kept for back-compat) ──────────────
// External consumers that pinned the prod collector URL keep working. New
// code should call `resolveCollectorUrl(env)` so the URL tracks the configured
// environment instead of being hard-pinned to production.

/** @deprecated Use `MONTILLA_HOSTS.production` or derive from environment. */
export const COLLECTOR_DOMAIN = 'montilla-rum.quero.space' as const
/** @deprecated Use `resolveCollectorUrl(env)`. Pinned to production. */
export const COLLECTOR_ENDPOINT = `https://${COLLECTOR_DOMAIN}${COLLECTOR_PATH}` as const

export const LOCATION_ENDPOINT = 'https://whereami.quero.workers.dev/' as const
export const LOCATION_STORAGE_KEY = 'location' as const
export const COOKIE_NAME = '_montilla_vid_v0' as const
export const IDENTITY_COOKIE_NAME = '_montilla_uid' as const
/**
 * Lean identity cookie holding `{ userId, email, phone }` as JSON, scoped to
 * the root domain so identity survives cross-subdomain navigation (e.g.
 * `querobolsa.com.br` → `checkout.querobolsa.com.br`) — same portability as
 * `_montilla_uid`. Only the principal keys are persisted; richer traits stay
 * in-memory for the live session and are NOT carried here.
 *
 * NOTE: unlike the former localStorage store, a cookie is sent to the server
 * on every same-domain request, so `email`/`phone` now travel in request
 * headers. Intentional trade-off for cross-subdomain identity.
 */
export const IDENTITY_DATA_COOKIE_NAME = '_montilla_idt' as const
/** @deprecated Legacy localStorage key — read only for one-time migration into {@link IDENTITY_DATA_COOKIE_NAME}. */
export const IDENTITY_STORAGE_KEY = 'montilla:identity' as const
export const APP_KEY = 'montilla' as const

/**
 * MontillaAds — URL param carrying the opaque ad link id (e.g.
 * `?mad=mad.quero-bolsa.black-friday._.k3f9ab`). Namespaced so it never
 * collides with a client's own `utm_*`. Overridable via `config.ads.param`.
 */
export const MONTILLA_AD_PARAM = 'mad' as const
/**
 * First-touch ad attribution cookie (JSON), scoped to the root domain so the
 * originating ad survives cross-subdomain navigation and later conversions —
 * same portability as {@link IDENTITY_DATA_COOKIE_NAME}.
 */
export const MONTILLA_AD_COOKIE_NAME = '_montilla_mad' as const

/**
 * First-touch acquisition context (referrer + utm_* + gclid), persisted to
 * sessionStorage so it stays fixed for the whole visit — the session's origin
 * is a fact set at entry and must not be overwritten by later navigations.
 */
export const ACQUISITION_STORAGE_KEY = 'montilla:acquisition' as const

/**
 * Cookie do Google Analytics que carrega o Client ID (`GA1.1.<id>.<ts>`).
 * Escrito pelo gtag/GA, NUNCA por este SDK — o montilla só lê, para poder
 * amarrar o evento à mesma pessoa vista no GA4.
 */
export const GA_COOKIE_NAME = '_ga' as const
